import { useSnapshot } from "valtio";
import { useWorkflowAccess } from "../../workflow/useWorkflowAccess";
import { workflowStore } from "../../workflow/workflowStore";
import {
  getWorkflow,
  getWorkflowDisplayGroups,
  NOTES_SLUG,
  OVERVIEW_SLUG,
  WorkflowItemDef,
} from "../../workflow/workflowItems";
import "./workflowSidebar.scss";

type Props = {
  engagementType: string;
  activeSlug: string;
  onNavigate: (slug: string) => void;
  /** Tools live outside the engagement, so the host owns that navigation. */
  onOpenJobs: () => void;
};

export function WorkflowSidebar({ engagementType, activeSlug, onNavigate, onOpenJobs }: Props) {
  const workflowSnap = useSnapshot(workflowStore);
  const canSee = useWorkflowAccess();
  // Filtered before anything counts or renders, so a restricted item cannot leak into the
  // rail, the "up next" marker or the denominator.
  const stages = getWorkflow(engagementType).map((stage) => ({
    ...stage,
    items: stage.items.filter(canSee),
  }));

  // A preview page sits in its stage but carries no completion state, so it must not
  // move the denominator — see WorkflowItemDef.preview.
  const progressItems = stages
    .filter((stage) => stage.countsTowardProgress)
    .flatMap((stage) => stage.items)
    .filter((i) => !i.preview);
  const completeCount = progressItems.filter(
    (i) => workflowSnap.statusByKey[i.key]?.status === "complete"
  ).length;
  const nextItem = progressItems.find(
    (i) => workflowSnap.statusByKey[i.key]?.status === "incomplete"
  );

  const dotClass = (workflowItem: WorkflowItemDef) => {
    // Nothing reports on a preview page, so it gets the neutral dot rather than reading
    // as permanently not-started.
    if (workflowItem.preview) return "dot-neutral";
    const status = workflowSnap.statusByKey[workflowItem.key];
    if (workflowSnap.loadFailed || !status) return "dot-neutral";
    if (status.status === "complete") return "dot-done";
    // Set up but missing data — flagged in its own orange, distinct from the amber the
    // "next step to do" dot uses, so the two don't read as the same thing.
    if (status.status === "attention") return "dot-attention";
    return workflowItem.key === nextItem?.key ? "dot-next" : "dot-todo";
  };

  return (
    <nav className="workflow-sidebar">
      <button
        className={`sidebar-item overview-item ${activeSlug === OVERVIEW_SLUG ? "active" : ""}`}
        onClick={() => onNavigate(OVERVIEW_SLUG)}
      >
        Overview
      </button>

      {!workflowSnap.loadFailed && (
        <div className="progress-summary">
          <span>
            {completeCount} of {progressItems.length} complete
          </span>
          <div className="bar">
            <div
              className="fill"
              style={{ width: `${(completeCount / Math.max(progressItems.length, 1)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {getWorkflowDisplayGroups(engagementType).map((group) => (
        <div key={group.key} className="stage-group">
          <div className="stage-label">{group.name}</div>
          {group.items.map((workflowItem) => (
            <button
              key={workflowItem.key}
              className={`sidebar-item ${activeSlug === workflowItem.slug ? "active" : ""}`}
              onClick={() => onNavigate(workflowItem.slug)}
            >
              <span className={`dot ${dotClass(workflowItem)}`} />
              {workflowItem.name}
            </button>
          ))}
        </div>
      ))}

      <div className="sidebar-divider" />
      <div className="stage-label">Tools</div>
      <button
        className={`sidebar-item ${activeSlug === NOTES_SLUG ? "active" : ""}`}
        onClick={() => onNavigate(NOTES_SLUG)}
      >
        <i className="bi bi-journal-text sidebar-icon" />
        Notes
      </button>
      <button className="sidebar-item" onClick={onOpenJobs}>
        <i className="bi bi-hdd-stack sidebar-icon" />
        Jobs
      </button>
    </nav>
  );
}
